import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Typography, Switch, FormControlLabel,
  TextField, MenuItem, Alert, Stack, Chip, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { setCallAiContext, getMyAiSharing, setMyAiSharing } from '../../services/callsApi';

/**
 * What the AI Supporter sees of this session (29 Sep 2026).
 *
 * The AI Supporter gets the clinical note, the client-facing summary, themes and risk
 * from recent sessions, and shares only client-facing content with the client. A
 * therapist can leave one session out, or set a default for all their sessions.
 * Risk recorded on a session always reaches the AI.
 */
const DEFAULT_OPTIONS = [
  { value: 'inherit', label: 'Follow the service setting' },
  { value: 'on', label: 'Share my sessions with the AI Supporter' },
  { value: 'off', label: 'Do not share my sessions' }
];

const toOption = (v) => (v === true ? 'on' : v === false ? 'off' : 'inherit');
const fromOption = (o) => (o === 'on' ? true : o === 'off' ? false : null);

export default function CallAiContextSection({ token, liveCallId, aiContext }) {
  const [withheld, setWithheld] = useState(!!aiContext?.withheld);
  const [reason, setReason] = useState(aiContext?.reason || '');
  const [myDefault, setMyDefault] = useState('inherit');
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    setWithheld(!!aiContext?.withheld);
    setReason(aiContext?.reason || '');
  }, [aiContext]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mine = await getMyAiSharing({ token });
        if (!cancelled) setMyDefault(toOption(mine.callContextForAI));
      } catch (_) { /* the default stays "follow the service setting" */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const saveCall = async (nextWithheld) => {
    setBusy('call');
    setNotice(null);
    try {
      await setCallAiContext({ token, liveCallId, withheld: nextWithheld, reason: nextWithheld ? reason : '' });
      setWithheld(nextWithheld);
      setNotice({ severity: 'success', text: nextWithheld ? 'This session is now left out of the AI Supporter\'s context. Any risk you recorded still reaches it.' : 'This session is back in the AI Supporter\'s context.' });
    } catch (err) {
      setNotice({ severity: 'error', text: err?.response?.data?.msg || err.message || 'Could not save' });
    } finally {
      setBusy(null);
    }
  };

  const saveDefault = async (option) => {
    setBusy('default');
    setNotice(null);
    try {
      const res = await setMyAiSharing({ token, callContextForAI: fromOption(option) });
      setMyDefault(toOption(res.callContextForAI));
      setNotice({ severity: 'success', text: 'Your default is saved. It applies to all your sessions.' });
    } catch (err) {
      setNotice({ severity: 'error', text: err?.response?.data?.msg || err.message || 'Could not save' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Accordion sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1">AI Supporter</Typography>
          <Chip size="small" label={withheld ? 'left out' : 'included'} color={withheld ? 'default' : 'primary'} variant="outlined" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Between sessions the AI Supporter sees your clinical note, the client-facing summary,
          themes and any risk from recent sessions, so the client does not have to repeat
          themselves and the AI supports your plan. It uses your note for understanding only
          and shares only client-facing content with the client.
        </Typography>
        {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}

        <FormControlLabel
          control={
            <Switch
              id={`call-ai-context-${liveCallId}`}
              checked={!withheld}
              disabled={!!busy}
              onChange={(e) => saveCall(!e.target.checked)}
            />
          }
          label={busy === 'call' ? <CircularProgress size={16} /> : 'Include this session'}
        />
        {!withheld && (
          <TextField
            id={`call-ai-context-reason-${liveCallId}`}
            size="small" fullWidth sx={{ mt: 1, mb: 2 }}
            label="Reason, if you leave this session out (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        )}
        {withheld && aiContext?.reason && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Reason: {aiContext.reason}
          </Typography>
        )}

        <TextField
          id="my-call-ai-sharing"
          select size="small" fullWidth sx={{ mt: 1 }}
          label="Default for all my sessions"
          value={myDefault}
          disabled={!!busy}
          onChange={(e) => saveDefault(e.target.value)}
          helperText="Risk you record always reaches the AI Supporter, whatever this is set to."
        >
          {DEFAULT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      </AccordionDetails>
    </Accordion>
  );
}
